import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useOrders, useSales, useShops } from '../services/api';
import { filterByDateRange } from '../utils/dateFilter';
import { printOrders, formatPrintDate } from '../utils/print';

const getOrderInvoiceNumber = (order) =>
  order?.invoiceNumber ?? order?.invoice_number ?? null;

const todayDateString = () => new Date().toISOString().split('T')[0];

const SalesRepOrdersPage = () => {
  const { saleId } = useParams();
  const navigate = useNavigate();

  const [dateFrom, setDateFrom] = useState(() => todayDateString());
  const [dateTo, setDateTo] = useState(() => todayDateString());
  const [shopFilter, setShopFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const orderParams = {
    sales_id: saleId,
    page: page + 1,
    per_page: rowsPerPage,
    ...(shopFilter ? { shop_id: shopFilter } : {}),
  };

  const { data: ordersData, isLoading } = useOrders(orderParams, { skipDateDefaults: true });
  const { data: sales = [] } = useSales();
  const { data: shops = [] } = useShops();
  const sale = sales.find((s) => String(s.id) === String(saleId));

  const repShops = useMemo(
    () =>
      shops.filter(
        (shop) =>
          String(shop.sale?.id ?? shop.saleId) === String(saleId)
      ),
    [shops, saleId]
  );

  const displayOrders = useMemo(
    () =>
      filterByDateRange(
        ordersData?.orders || [],
        (order) => order.createdAt,
        dateFrom,
        dateTo
      ),
    [ordersData, dateFrom, dateTo]
  );

  const pageTotalAmount = useMemo(
    () => displayOrders.reduce((sum, o) => sum + parseFloat(o.totalAmount ?? 0), 0),
    [displayOrders]
  );

  const meta = ordersData?.meta || {};

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getOrdersByIds = (ids) =>
    (ordersData?.orders || []).filter((o) => ids.includes(o.id));

  const handlePrintOrders = (orders) => {
    try {
      const label = sale
        ? `Sales rep: ${sale.user?.fullName || sale.code} (${dateFrom} – ${dateTo})`
        : '';
      printOrders(orders, `Orders — ${label}`);
    } catch (err) {
      showSnackbar(err.message, 'error');
    }
  };

  const handlePrintSelected = () => {
    const orders = getOrdersByIds(selectedOrderIds);
    if (!orders.length) {
      showSnackbar('Select at least one order to print', 'warning');
      return;
    }
    handlePrintOrders(orders);
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  const handleSelectAll = () => {
    if (selectedOrderIds.length === displayOrders.length) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(displayOrders.map((o) => o.id));
    }
  };

  const handleShopChange = (e) => {
    setShopFilter(e.target.value);
    setPage(0);
    setSelectedOrderIds([]);
  };

  const getStatusColor = (isApproved) => {
    if (isApproved === true) return 'success';
    if (isApproved === false) return 'warning';
    return 'default';
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => navigate(`/sales/${saleId}/return-invoice`)} aria-label="Back">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" component="h1">
                Sales Rep Orders
              </Typography>
              {sale && (
                <Typography variant="body2" color="text.secondary">
                  {sale.user?.fullName || sale.code} ({sale.code})
                </Typography>
              )}
            </Box>
          </Box>
          <Button
            variant="contained"
            color="info"
            startIcon={<PrintIcon />}
            onClick={handlePrintSelected}
            disabled={selectedOrderIds.length === 0}
          >
            Print Selected
          </Button>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Filters
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Shop</InputLabel>
                  <Select
                    value={shopFilter}
                    label="Shop"
                    onChange={handleShopChange}
                  >
                    <MenuItem value="">All shops</MenuItem>
                    {repShops.map((shop) => (
                      <MenuItem key={shop.id} value={shop.id}>
                        {shop.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="From"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setSelectedOrderIds([]);
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="To"
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setSelectedOrderIds([]);
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  {displayOrders.length} on this page (date filtered)
                </Typography>
                <Typography variant="h6" color="primary">
                  Page total: {pageTotalAmount.toFixed(2)} JD
                </Typography>
                {meta.total != null && (
                  <Typography variant="caption" color="text.secondary">
                    {meta.total} order(s) from API (all dates)
                  </Typography>
                )}
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Sales rep and shop use the API. Date range is filtered on the client for the current page only.
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            {isLoading ? (
              <LinearProgress />
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={
                              selectedOrderIds.length > 0 &&
                              selectedOrderIds.length < displayOrders.length
                            }
                            checked={
                              displayOrders.length > 0 &&
                              selectedOrderIds.length === displayOrders.length
                            }
                            onChange={handleSelectAll}
                            disabled={displayOrders.length === 0}
                          />
                        </TableCell>
                        <TableCell>Shop</TableCell>
                        <TableCell>Invoice #</TableCell>
                        <TableCell>Area</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayOrders.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            No orders match the filters on this page
                          </TableCell>
                        </TableRow>
                      ) : (
                        displayOrders.map((order) => (
                          <TableRow key={order.id} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedOrderIds.includes(order.id)}
                                onChange={() => handleSelectOrder(order.id)}
                              />
                            </TableCell>
                            <TableCell>{order.shop?.name || 'N/A'}</TableCell>
                            <TableCell>{getOrderInvoiceNumber(order) ?? '—'}</TableCell>
                            <TableCell>{order.shop?.ammanArea?.name || 'N/A'}</TableCell>
                            <TableCell align="right">{order.totalAmount} JD</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={order.isApproved ? 'Approved' : 'Pending'}
                                color={getStatusColor(order.isApproved)}
                              />
                            </TableCell>
                            <TableCell>{formatPrintDate(order.createdAt)}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                color="primary"
                                title="View items"
                                onClick={() => {
                                  setSelectedOrder(order);
                                  setDetailsOpen(true);
                                }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="info"
                                title="Print"
                                onClick={() => handlePrintOrders([order])}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  count={meta.total ?? 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, newPage) => {
                    setPage(newPage);
                    setSelectedOrderIds([]);
                  }}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                    setSelectedOrderIds([]);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Order details</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Order ID</Typography>
                  <Typography variant="body2">{selectedOrder.id}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Invoice #</Typography>
                  <Typography variant="body2">
                    {getOrderInvoiceNumber(selectedOrder) ?? '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Shop</Typography>
                  <Typography>{selectedOrder.shop?.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Total</Typography>
                  <Typography variant="h6" color="primary">
                    {selectedOrder.totalAmount} %
                  </Typography>
                </Grid>
              </Grid>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Qty</TableCell>
                      <TableCell>Unit price</TableCell>
                      <TableCell>Discount</TableCell>
                      <TableCell>Line total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOrder.items?.map((item) => {
                      const unitPrice = parseFloat(item.unit_price ?? 0);
                      const qty = item.quantity ?? 0;
                      const discount = parseFloat(item.discount ?? 0);
                      const lineTotal = (unitPrice * qty - (discount * unitPrice /100)).toFixed(2);
                      return (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Typography variant="body2">{item.product_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.product_name_arabic}
                            </Typography>
                          </TableCell>
                          <TableCell>{qty}</TableCell>
                          <TableCell>{unitPrice} JD</TableCell>
                          <TableCell>{discount} JD</TableCell>
                          <TableCell>{lineTotal} JD</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<PrintIcon />}
            onClick={() => selectedOrder && handlePrintOrders([selectedOrder])}
          >
            Print
          </Button>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SalesRepOrdersPage;
